import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  BrowseToolbar, SpacerRow, interleaveDetail, useBrowseVirtual,
} from '@components/lookup/browse-kit';
import { GridFrame, GridHeadRow, GridToolbar, gridCell } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import { multiSort } from '@lib/grid';
import { SlicerRail } from '@components/lookup/SlicerRail';
import { pcpDetail, pcpList, pcpListExport } from '@lib/care-api';
import {
  EMPTY_LOOKUP_FILTERS, activeFilterCount, buildPcpAxes, matchPcpOps, type LookupFilters,
} from '@lib/lookup-axes';
import { fmtMemberId } from '@lib/member-id';
import type { PcpBrowseRow, PcpDetailResp, PcpListResp, EmptyReason } from '@lib/care-model';
import { EMPTY_REASON_META, emptyNotice, emptyRow, emptyTone } from '@lib/care-model';
import { isValidReason, useSessionReason } from '@lib/useSessionReason';

/**
 * PcpLookupSection - the PCP tab as a browse-all table (2026-07-14 rework, Portal pcp-list parity),
 * brought to the Portal's as-built shape on 19 Sep 2026 (row 418, Frontend's spec section 3 and
 * MemberLookup.tsx PcpBrowseSection / PcpSection): every PCP with a panel, one aggregated KPI row
 * each (PCP name and NPI, Panel, Delivered, Returned, Resulted, Letters, MSO, IPA, Market, Last
 * activity), loaded once behind the session reason, searched ("PCP name or NPI") and sorted
 * client-side, virtualized, with a leading toggle column. The expand shows the PCP card, the panel's
 * members (each a JUMP INTO THE MEMBER TAB, the Portal's drill-in) with a kit-stage pill, and the
 * PCP's communications (the letters and faxes sent for the panel), each half named when the campaign
 * database lacks its table. Export to Excel exports exactly the rows on screen, behind the page's
 * export-scoped re-prompt. A settled search and a committed filter change each fire one audit beacon.
 */

type ColKey =
  | 'pcp' | 'panel' | 'delivered' | 'returned' | 'resulted' | 'letters'
  | 'mso' | 'ipa' | 'market' | 'last';

/** The PCP tab's columns in the Portal's order and wording (PcpBrowseSection COLS). */
export const PCP_COLUMNS: ReadonlyArray<{ key: ColKey; label: string }> = [
  { key: 'pcp', label: 'PCP' },
  { key: 'panel', label: 'Panel' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'returned', label: 'Returned' },
  { key: 'resulted', label: 'Resulted' },
  { key: 'letters', label: 'Letters' },
  { key: 'mso', label: 'MSO' },
  { key: 'ipa', label: 'IPA' },
  { key: 'market', label: 'Market' },
  { key: 'last', label: 'Last activity' },
];

/** Row key = the aggregation group key (NPI-first, name fallback) - unique per row by construction,
 *  and the key the export posts (pcp-list.ts pcpRowKey, pinned in lockstep). */
export function pcpRowKey(r: PcpBrowseRow): string {
  return r.pcp_npi ? `npi:${r.pcp_npi}` : `name:${r.pcp_name}`;
}

/** The Portal's search haystack for the PCP tab: name and NPI. */
export function pcpHay(r: PcpBrowseRow): string {
  return `${r.pcp_name} ${r.pcp_npi}`.toLowerCase();
}

function sortValue(r: PcpBrowseRow, key: ColKey): string | number {
  switch (key) {
    case 'pcp': return (r.pcp_name || '').toLowerCase();
    case 'panel': return r.panel_size;
    case 'delivered': return r.delivered;
    case 'returned': return r.returned;
    case 'resulted': return r.resulted;
    case 'letters': return r.with_letter;
    case 'mso': return (r.top_mso || '').toLowerCase();
    case 'ipa': return (r.top_ipa || '').toLowerCase();
    case 'market': return (r.top_market || '').toLowerCase();
    case 'last': return r.last_activity || '';
  }
}

/** The Portal's kit-stage pill vocabulary for member-shaped tables (status-pills.ts kitStageToPill). */
export function kitStagePill(kitStage: string | null | undefined): { label: string; cls: string } | null {
  switch ((kitStage ?? '').trim()) {
    case 'Sample resulted': return { label: 'Resulted', cls: 'pill pill-success' };
    case 'Sample returned': return { label: 'Returning', cls: 'pill pill-warning' };
    case 'Kit delivered': return { label: 'Delivered', cls: 'pill pill-primary' };
    case 'Kit shipped': return { label: 'Shipped', cls: 'pill pill-primary' };
    case 'Kit prepared': return { label: 'Prepared', cls: 'pill' };
    default: return null;
  }
}

const dash = <span className="muted">-</span>;

export interface PcpLookupSectionProps {
  onNeedReason: () => void;
  onRequireExportReason: (proceed: (reason: string) => void) => void;
  onAuditSearch?: (query: string) => void;
  onAuditFilter?: (filters: Record<string, string[]>) => void;
  /** A panel member jumps into the Member tab (the Portal's drill-in). */
  onJumpToMember?: (memberId: string) => void;
  /** A PCP to open on arrival (a member's PCP jump), as the row key `npi:<npi>` / `name:<name>`. */
  focusPcp?: string | null;
  onFocusHandled?: () => void;
}

export function PcpLookupSection(props: PcpLookupSectionProps) {
  const { onNeedReason, onRequireExportReason, onAuditSearch, onAuditFilter, onJumpToMember, focusPcp, onFocusHandled } = props;
  const sessionReason = useSessionReason();
  const reasonValid = isValidReason(sessionReason);

  const [rows, setRows] = useState<PcpBrowseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [available, setAvailable] = useState(true);
  const [emptyReason, setEmptyReason] = useState<EmptyReason | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [loadedFor, setLoadedFor] = useState('');

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [filters, setFilters] = useState<LookupFilters>(EMPTY_LOOKUP_FILTERS);

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [pendingScroll, setPendingScroll] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, PcpDetailResp | 'loading' | 'error'>>({});
  const [note, setNote] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState('');

  // Load the whole PCP roster once per valid session reason.
  useEffect(() => {
    if (!reasonValid || loadedFor === sessionReason) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr('');
    void pcpList(sessionReason)
      .then((r) => {
        if (cancelled) return;
        if (r.ok) {
          const d = r.data as PcpListResp;
          const list = Array.isArray(d.rows) ? d.rows : [];
          setRows(list);
          setTotal(d.total ?? list.length);
          setAvailable(d.available !== false);
          setEmptyReason(d.emptyReason);
          setLoadedFor(sessionReason);
        } else {
          setLoadErr(`PCP roster load failed (HTTP ${r.status}).`);
        }
        // The load is over on BOTH branches, set HERE rather than in a finally: a finally runs a
        // microtask after the state above, by which time React may have re-run this effect (loadedFor
        // changed) and flipped `cancelled`, leaving `loading` stuck true (found by the sections test).
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadErr('PCP roster load failed.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reasonValid, sessionReason, loadedFor]);

  // The beacons: one per settled non-empty search; one per committed filter change.
  useEffect(() => {
    const q = deferredSearch.trim();
    if (q) onAuditSearch?.(q);
  }, [deferredSearch]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtersSeen = useRef(false);
  useEffect(() => {
    if (!filtersSeen.current) {
      filtersSeen.current = true;
      if (activeFilterCount(filters) === 0) return;
    }
    onAuditFilter?.(filters);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rail filters (panel-contains) + search + sort in memory (Portal PCP browse).
  const railFiltered = useMemo(
    () => (Object.keys(filters).length ? rows.filter((r) => matchPcpOps(r, filters)) : rows),
    [rows, filters],
  );

  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return railFiltered;
    return railFiltered.filter((r) => pcpHay(r).includes(q));
  }, [railFiltered, deferredSearch]);

  const axes = useMemo(() => buildPcpAxes(rows), [rows]);

  const toggleRow = async (r: PcpBrowseRow) => {
    const key = pcpRowKey(r);
    if (openKey === key) {
      setOpenKey(null);
      return;
    }
    if (!r.pcp_npi && !r.pcp_name) {
      setNote('This row has no NPI or name to key a panel reveal on.');
      return;
    }
    setOpenKey(key);
    if (details[key] && details[key] !== 'error') return;
    setDetails((prev) => ({ ...prev, [key]: 'loading' }));
    try {
      const resp = await pcpDetail(r.pcp_npi ? { npi: r.pcp_npi } : { name: r.pcp_name }, sessionReason);
      if (resp.ok && (resp.data as PcpDetailResp).found) {
        setDetails((prev) => ({ ...prev, [key]: resp.data as PcpDetailResp }));
      } else {
        setDetails((prev) => ({ ...prev, [key]: 'error' }));
        setNote(`Panel unavailable for ${r.pcp_name || 'PCP'} (HTTP ${resp.status}).`);
      }
    } catch {
      setDetails((prev) => ({ ...prev, [key]: 'error' }));
      setNote(`Panel unavailable for ${r.pcp_name || 'PCP'}.`);
    }
  };

  // A member's PCP jump: open that PCP's row once the roster is on hand and scroll it into view.
  // The toggle column lives inside a memoized `columns` array (row 356's grid habit), whose deps are
  // the open row and the loaded details, NOT the session reason. A closure captured there before the
  // grant would call the reveal with an EMPTY reason and be refused by the server (found by the shell
  // test on 19 Sep 2026: memberDetail called with ''). The column therefore calls through a ref that
  // always holds the current toggleRow.
  const toggleRef = useRef(toggleRow);
  toggleRef.current = toggleRow;
  useEffect(() => {
    if (!focusPcp || !reasonValid || loading) return;
    if (rows.length === 0 && !loadErr) return;
    const hit = rows.find((r) => pcpRowKey(r) === focusPcp)
      // A key built from the member record may carry the name where the roster row carries an NPI.
      ?? rows.find((r) => focusPcp.startsWith('name:') && `name:${r.pcp_name}` === focusPcp);
    if (hit) {
      setSearch('');
      setFilters(EMPTY_LOOKUP_FILTERS);
      setPendingScroll(pcpRowKey(hit));
      if (openKey !== pcpRowKey(hit)) void toggleRow(hit);
    } else {
      setNote(`PCP ${focusPcp.replace(/^(npi|name):/, '')} is not on this roster.`);
    }
    onFocusHandled?.();
  }, [focusPcp, reasonValid, loading, rows, loadErr]); // eslint-disable-line react-hooks/exhaustive-deps

  const ellipsis = { maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;
  const columns = useMemo<GridColumn<PcpBrowseRow>[]>(() => {
    // First click ascending on every header (the Portal's rule): no firstDir.
    const num = (key: ColKey, label: string, get: (r: PcpBrowseRow) => number | string): GridColumn<PcpBrowseRow> => ({
      key, label, align: 'right', tdClassName: 'small num-cell', sortValue: (r) => sortValue(r, key), render: get,
    });
    return [
      {
        key: 'toggle', label: '', menuLabel: 'Expand', locked: true, stopRowClick: true, defaultWidth: 36,
        thStyle: { width: 36 }, tdStyle: { textAlign: 'center' },
        render: (r) => {
          const key = pcpRowKey(r);
          const open = openKey === key;
          return (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ padding: '0 6px' }}
              aria-expanded={open}
              aria-label={open ? 'Hide PCP detail' : 'Show PCP detail'}
              title={open ? 'Hide PCP detail' : 'Show PCP detail'}
              onClick={() => void toggleRef.current(r)}
            >
              {details[key] === 'loading' ? '...' : open ? '▾' : '▸'}
            </button>
          );
        },
      },
      {
        key: 'pcp', label: 'PCP', tdStyle: { whiteSpace: 'nowrap' }, sortValue: (r) => sortValue(r, 'pcp'),
        render: (r) => (
          <>
            <b>{r.pcp_name || 'PCP'}</b>
            <span className="muted small" style={{ marginLeft: 6, fontFamily: 'var(--font-mono)' }}>
              {r.pcp_npi || 'no NPI'}
            </span>
          </>
        ),
      },
      num('panel', 'Panel', (r) => r.panel_size.toLocaleString()),
      num('delivered', 'Delivered', (r) => r.delivered),
      num('returned', 'Returned', (r) => r.returned),
      num('resulted', 'Resulted', (r) => r.resulted),
      num('letters', 'Letters', (r) => r.with_letter),
      { key: 'mso', label: 'MSO', tdClassName: 'small', tdStyle: ellipsis, sortValue: (r) => sortValue(r, 'mso'), render: (r) => r.top_mso || dash },
      { key: 'ipa', label: 'IPA', tdClassName: 'small', tdStyle: ellipsis, sortValue: (r) => sortValue(r, 'ipa'), render: (r) => r.top_ipa || dash },
      { key: 'market', label: 'Market', tdClassName: 'small', sortValue: (r) => sortValue(r, 'market'), render: (r) => r.top_market || dash },
      {
        key: 'last', label: 'Last activity', tdClassName: 'small', tdStyle: { whiteSpace: 'nowrap' },
        sortValue: (r) => sortValue(r, 'last'), render: (r) => r.last_activity || dash,
      },
    ];
  }, [details, openKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const view = useGridView('lookup-pcps', columns, { defaultSort: [{ k: 'panel', asc: false }] });
  const byKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const sorted = useMemo(
    () => multiSort(filtered, view.sort, (r, k) => byKey.get(k)?.sortValue?.(r)),
    [filtered, view.sort, byKey],
  );
  const span = Math.max(1, view.visible.length);

  const displayItems = useMemo(
    () => interleaveDetail(sorted, (r) => pcpRowKey(r) === openKey),
    [sorted, openKey],
  );
  const virtual = useBrowseVirtual(displayItems, (it) => `${it.kind}:${pcpRowKey(it.row)}`, 560);

  useEffect(() => {
    if (!pendingScroll) return;
    const idx = displayItems.findIndex((it) => it.kind === 'row' && pcpRowKey(it.row) === pendingScroll);
    if (idx < 0) {
      if (!loading && rows.length > 0) setPendingScroll(null);
      return;
    }
    setPendingScroll(null);
    virtual.scrollToIndex(idx);
  }, [pendingScroll, displayItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const runExport = async (exportReason: string) => {
    setExporting(true);
    setExportErr('');
    try {
      const { blob, filename } = await pcpListExport(exportReason, sorted.map(pcpRowKey));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (!reasonValid) {
    return (
      <div className="note" style={{ marginBottom: 14 }}>
        Provide a reason-for-access to load the PCP roster.{' '}
        <button type="button" className="btn btn-sm" onClick={onNeedReason}>Provide reason</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
      <BrowseToolbar
        search={search}
        onSearch={setSearch}
        placeholder="PCP name or NPI"
        ariaLabel="Search the PCP roster"
        shown={sorted.length}
        total={rows.length}
        noun="PCPs"
        right={
          <button
            type="button"
            className="btn btn-sm"
            disabled={exporting || sorted.length === 0}
            title="Export the filtered PCPs (current view) to Excel"
            onClick={() => {
              setExportErr('');
              onRequireExportReason((exportReason) => { void runExport(exportReason); });
            }}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        }
      />
      {exportErr && <div className="note danger" style={{ marginBottom: 14 }}>{exportErr}</div>}

      {note && (
        <div className="note" style={{ marginBottom: 14 }}>
          {note}
        </div>
      )}
      {loadErr && (
        <div className="note danger" style={{ marginBottom: 14 }}>
          {loadErr}
        </div>
      )}
      {!available && !loading && !loadErr && (
        <div className={`note ${emptyTone(emptyReason)}`} style={{ marginBottom: 14 }}>
          {emptyNotice(emptyReason, 'PCP roster unavailable in this campaign DB.')}
        </div>
      )}
      {available && !loading && !loadErr && emptyReason && EMPTY_REASON_META[emptyReason].notice && (
        <div className={`note ${emptyTone(emptyReason)}`} style={{ marginBottom: 14 }}>
          {EMPTY_REASON_META[emptyReason].notice}
        </div>
      )}
      {rows.length < total && (
        <div className="muted small" style={{ marginBottom: 8 }}>
          Showing the first {rows.length.toLocaleString()} of {total.toLocaleString()} PCPs (server cap).
        </div>
      )}

      <GridFrame view={view}>
      <GridToolbar view={view} />
      <div ref={virtual.scrollRef} className="card card-flush" style={{ maxHeight: view.expanded ? 'calc(100vh - 90px)' : '65vh', overflow: 'auto' }}>
        <table className="dt">
          <thead>
            <GridHeadRow view={view} />
          </thead>
          <tbody>
            {displayItems.length === 0 && (
              <tr>
                <td colSpan={span} className="muted" style={{ textAlign: 'center', padding: '20px 0' }}>
                  {loading ? 'Loading PCPs...' : emptyRow(emptyReason, search.trim() ? 'No PCPs match this search.' : 'No PCPs found for this campaign.')}
                </td>
              </tr>
            )}
            <SpacerRow height={virtual.padTop} colSpan={span} />
            {virtual.items.map((vi) => {
              const item = displayItems[vi.index];
              if (!item) return null;
              const r = item.row;
              const key = pcpRowKey(r);
              if (item.kind === 'detail') {
                const d = details[key];
                return (
                  <tr key={vi.key} data-index={vi.index} ref={virtual.measureRef}>
                    <td colSpan={span} style={{ background: 'var(--paper-2)', padding: '12px 16px', height: 'auto' }}>
                      {d === 'loading' || d === undefined ? (
                        <span className="muted small">Loading the panel...</span>
                      ) : d === 'error' ? (
                        <span className="muted small">Panel unavailable.</span>
                      ) : d.pcp ? (
                        <PcpExpand d={d} onJumpToMember={onJumpToMember} />
                      ) : (
                        <span className="muted small">Panel unavailable.</span>
                      )}
                    </td>
                  </tr>
                );
              }
              const expanded = openKey === key;
              return (
                <tr
                  key={vi.key}
                  data-index={vi.index}
                  ref={virtual.measureRef}
                  tabIndex={0}
                  aria-expanded={expanded}
                  aria-label={`${r.pcp_name || 'PCP'} ${r.pcp_npi || ''}`.trim()}
                  onClick={() => void toggleRow(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void toggleRow(r);
                    }
                  }}
                  style={{ cursor: 'pointer', background: expanded ? 'var(--paper-2)' : undefined }}
                >
                  {view.visible.map((c) => (
                    <td key={c.key} {...gridCell(view, c, r)}>
                      {c.render(r, vi.index)}
                    </td>
                  ))}
                </tr>
              );
            })}
            <SpacerRow height={virtual.padBottom} colSpan={span} />
          </tbody>
        </table>
      </div>
      </GridFrame>
      </div>
      <SlicerRail
        axes={axes}
        filters={filters}
        onFilters={setFilters}
        scope={{ shown: railFiltered.length, total: rows.length }}
        noun="PCPs"
      />
    </div>
  );
}

/** The PCP expand: the card, the panel members (each a jump into the Member tab), the communications. */
function PcpExpand({ d, onJumpToMember }: { d: PcpDetailResp; onJumpToMember?: ((memberId: string) => void) | undefined }) {
  const pcp = d.pcp!;
  const comms = d.communications ?? null;
  const memberCell = (memberId: string, text: string) =>
    onJumpToMember ? (
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ padding: 0, fontFamily: 'inherit', fontSize: 'inherit', textDecoration: 'underline' }}
        title={`Look up member ${fmtMemberId(memberId)}`}
        onClick={() => onJumpToMember(memberId)}
      >
        {text}
      </button>
    ) : text;
  return (
    <>
      <div style={{ fontWeight: 600 }}>
        {pcp.name || 'PCP'}{' '}
        {pcp.npi && <span className="muted">NPI {pcp.npi}</span>}
      </div>
      <div className="small" style={{ marginTop: 4 }}>
        {[pcp.address, pcp.city, pcp.state, pcp.zip].filter(Boolean).join(', ') || <span className="muted">no address on file</span>}
        {pcp.fax && <> {'·'} fax {pcp.fax}</>}
        {pcp.phone && <> {'·'} {pcp.phone}</>}
      </div>

      <div style={{ marginTop: 10, fontWeight: 600 }}>
        Panel members ({d.panelTotal ?? d.panel.length})
      </div>
      {d.panelTotal > d.panel.length && (
        <div className="muted small" style={{ padding: '2px 0' }}>
          Showing the first {d.panel.length} of {d.panelTotal} panel members.
        </div>
      )}
      {d.panel.length === 0 ? (
        <div className="muted small" style={{ padding: '4px 0' }}>none</div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 4 }}>
          <table className="dt" data-testid="pcp-panel">
            <thead>
              <tr>
                <th>Member</th>
                <th>Name</th>
                <th>DOB</th>
                <th>Language</th>
                <th>Status</th>
                <th>Phone</th>
              </tr>
            </thead>
            <tbody>
              {d.panel.map((m) => {
                const pill = kitStagePill(m.kit_stage);
                return (
                  <tr key={m.member_id}>
                    <td className="small" style={{ whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)' }}>{memberCell(m.member_id, fmtMemberId(m.member_id))}</td>
                    <td style={{ whiteSpace: 'nowrap' }}><b>{memberCell(m.member_id, m.full_name || 'member')}</b></td>
                    <td className="small" style={{ whiteSpace: 'nowrap' }}>{m.dob || dash}</td>
                    <td className="small">{m.language || dash}</td>
                    <td className="small">{pill ? <span className={pill.cls}>{pill.label}</span> : dash}</td>
                    <td className="small" style={{ whiteSpace: 'nowrap' }}>{m.phone1 || dash}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 12, fontWeight: 600 }}>
        PCP communication ({comms ? comms.letters.length + comms.faxes.length : 0})
        <span className="muted small" style={{ marginLeft: 8, fontWeight: 400 }}>letters and faxes, newest first</span>
      </div>
      {!comms ? (
        <div className="muted small" style={{ padding: '4px 0' }}>Communications were not carried by this reveal.</div>
      ) : !comms.available ? (
        <div className="muted small" style={{ padding: '4px 0' }}>
          {comms.unavailable === 'capability-absent'
            ? 'This campaign database carries no PCP letters or fax tables (not part of this program).'
            : 'The communications could not be read.'}
        </div>
      ) : (
        <>
          {!comms.lettersAvailable && <div className="muted small">PCP letters: not part of this program.</div>}
          {!comms.faxesAvailable && <div className="muted small">Faxes: not part of this program.</div>}
          {comms.letters.length === 0 && comms.faxes.length === 0 ? (
            <div className="muted small" style={{ padding: '4px 0' }}>none</div>
          ) : (
            <div style={{ overflowX: 'auto', marginTop: 4 }}>
              <table className="dt" data-testid="pcp-comms">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Kind</th>
                    <th>Member</th>
                    <th>To</th>
                    <th>Result</th>
                    <th>Pages</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ...comms.faxes.map((f) => ({ when: f.ts, kind: 'Fax', memberId: f.member_id, name: f.full_name, to: f.to_number, result: f.result, pages: f.pages, detail: f.sub_result || f.subject, key: `fax-${f.fax_id}` })),
                    ...comms.letters.map((l) => ({ when: l.pdf_mtime, kind: 'Letter', memberId: l.member_id, name: l.full_name, to: l.phone, result: l.delivery_status || 'Letter', pages: '', detail: l.delivered_ts ? `delivered ${l.delivered_ts}` : '', key: `letter-${l.letter_id}` })),
                  ]
                    .sort((a, b) => b.when.localeCompare(a.when))
                    .map((e) => (
                      <tr key={e.key}>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>{e.when || dash}</td>
                        <td className="small">{e.kind}</td>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>
                          {e.memberId ? memberCell(e.memberId, `${e.name || 'member'} ${fmtMemberId(e.memberId)}`) : dash}
                        </td>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>{e.to || dash}</td>
                        <td className="small">{e.result || dash}</td>
                        <td className="small num-cell">{e.pages || dash}</td>
                        <td className="small">{e.detail || dash}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
          {comms.truncated && <div className="muted small">The communications list was capped; the newest are shown.</div>}
        </>
      )}
    </>
  );
}
